import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { uploadToGallery } from "@/lib/gallery";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Star, Eye, EyeOff, Check, X, Image as ImageIcon, MessageSquare, ExternalLink, Crown } from "lucide-react";

const thumbOf = (m) => m.media_type === "video" && m.url.includes("/video/upload/")
  ? m.url.replace("/video/upload/", "/video/upload/so_0,w_400/").replace(/\.[^./]+$/, ".jpg")
  : m.url;

function AlbumManager({ album, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);
  const load = () => api.get(`/gallery/admin/albums/${album.id}/media`).then((r) => setDetail(r.data));
  useEffect(() => { load(); }, [album.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkUpload = async (files) => {
    const items = [];
    let n = 0;
    for (const f of Array.from(files).slice(0, 30)) {
      n += 1;
      try {
        setUploading(n);
        const up = await uploadToGallery(f);
        items.push(up);
      } catch { toast.error(`Failed: ${f.name}`); }
    }
    setUploading(0);
    if (items.length) {
      await api.post(`/gallery/admin/albums/${album.id}/media`, { items });
      toast.success(`${items.length} file${items.length > 1 ? "s" : ""} added to ${album.title}`);
      load(); onChanged();
    }
  };
  const mod = async (mid, body, msg) => { await api.put(`/gallery/admin/media/${mid}`, body); toast.success(msg); load(); onChanged(); };
  const delMedia = async (mid) => { if (!window.confirm("Delete this file from the gallery?")) return; await api.delete(`/gallery/admin/media/${mid}`); load(); onChanged(); };
  const setCover = async (url) => { await api.put(`/gallery/admin/albums/${album.id}`, { cover_url: url }); toast.success("Cover photo set"); onChanged(); };
  const modComment = async (c) => { await api.put(`/gallery/admin/comments/${c.id}`, { hidden: !c.hidden }); load(); };
  const delComment = async (c) => { if (!window.confirm("Delete this comment permanently?")) return; await api.delete(`/gallery/admin/comments/${c.id}`); load(); };

  if (!detail) return <div className="text-zinc-600 text-xs py-3 animate-pulse">Loading media…</div>;
  const pending = detail.media.filter((m) => m.status === "pending");
  const approved = detail.media.filter((m) => m.status === "approved");
  return (
    <div className="border-t border-pb-border/50 pt-3 mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden"
          onChange={(e) => e.target.files.length && bulkUpload(e.target.files)} data-testid={`gal-upload-input-${album.id}`} />
        <button data-testid={`gal-upload-btn-${album.id}`} disabled={!!uploading} onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {uploading ? `Uploading file ${uploading}…` : "Bulk Upload Photos / Videos"}
        </button>
        <a href={`/gallery/${album.id}`} target="_blank" rel="noreferrer" data-testid={`gal-view-${album.id}`}
          className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> View public page
        </a>
      </div>

      {pending.length > 0 && (
        <div data-testid={`gal-pending-${album.id}`}>
          <div className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400 mb-2">Fan submissions awaiting approval ({pending.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {pending.map((m) => (
              <div key={m.id} className="relative border border-amber-500/40">
                <img src={thumbOf(m)} alt="" className="w-full h-24 object-cover" />
                <div className="absolute bottom-0 inset-x-0 flex">
                  <button data-testid={`gal-approve-${m.id}`} onClick={() => mod(m.id, { status: "approved" }, "Approved — now live")}
                    className="flex-1 bg-[#A3E635] text-black py-1 flex items-center justify-center"><Check className="w-3.5 h-3.5" /></button>
                  <button data-testid={`gal-reject-${m.id}`} onClick={() => delMedia(m.id)}
                    className="flex-1 bg-pb-red/90 text-black py-1 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>
                <div className="absolute top-0 inset-x-0 bg-black/70 text-[8px] text-zinc-300 px-1 truncate">{m.submitted_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-2">Live media ({approved.length}) — hover to set cover, caption or delete</div>
        {approved.length === 0 ? <div className="text-zinc-600 text-xs">Nothing here yet — upload above.</div> : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {approved.map((m) => (
              <div key={m.id} className="relative group border border-pb-border/60" data-testid={`gal-media-${m.id}`}>
                <img src={thumbOf(m)} alt="" className="w-full h-20 object-cover" />
                {album.cover_url === m.url && <span className="absolute top-1 left-1 bg-yellow-500 text-black text-[8px] font-black px-1">COVER</span>}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                  <button title="Set as cover" data-testid={`gal-cover-${m.id}`} onClick={() => setCover(m.url)}
                    className="w-6 h-6 border border-yellow-500 text-yellow-400 flex items-center justify-center"><Crown className="w-3 h-3" /></button>
                  <button title="Caption" data-testid={`gal-caption-${m.id}`}
                    onClick={() => { const cap = window.prompt("Caption for this shot:", m.caption || ""); if (cap !== null) mod(m.id, { caption: cap }, "Caption saved"); }}
                    className="w-6 h-6 border border-pb-cyan text-pb-cyan flex items-center justify-center"><ImageIcon className="w-3 h-3" /></button>
                  <button title="Delete" data-testid={`gal-del-${m.id}`} onClick={() => delMedia(m.id)}
                    className="w-6 h-6 border border-pb-red text-pb-red flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Comments ({detail.comments.length})</div>
        {detail.comments.length === 0 ? <div className="text-zinc-600 text-xs">No comments yet.</div> : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {detail.comments.map((c) => (
              <div key={c.id} className={`flex items-start gap-2 border px-3 py-2 text-xs ${c.hidden ? "border-pb-border/40 opacity-50" : "border-pb-border/70"}`} data-testid={`gal-comment-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-pb-cyan font-bold uppercase text-[10px] mr-2">{c.author_name}</span>
                  <span className="text-zinc-300">{c.text}</span>
                  {c.hidden && <span className="ml-2 text-[9px] uppercase text-amber-400">hidden</span>}
                </div>
                <button data-testid={`gal-comment-hide-${c.id}`} title={c.hidden ? "Unhide" : "Hide"} onClick={() => modComment(c)}
                  className="text-zinc-500 hover:text-amber-400">{c.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
                <button data-testid={`gal-comment-del-${c.id}`} title="Delete" onClick={() => delComment(c)}
                  className="text-zinc-500 hover:text-pb-red"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GalleryControl() {
  const [albums, setAlbums] = useState([]);
  const [tourneys, setTourneys] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [nt, setNt] = useState({ title: "", tournament_id: "" });
  const load = () => api.get("/gallery/admin/albums").then((r) => setAlbums(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/tournaments").then((r) => setTourneys(r.data || [])).catch(() => {});
  }, []);

  const create = async () => {
    if (!nt.title.trim() && !nt.tournament_id) return toast.error("Pick a tournament or type a title");
    const t = tourneys.find((x) => x.id === nt.tournament_id);
    await api.post("/gallery/admin/albums", { title: nt.title.trim() || t?.name || "", tournament_id: nt.tournament_id || null });
    toast.success("Album created — upload photos, then publish it");
    setNt({ title: "", tournament_id: "" }); setCreating(false); load();
  };
  const upd = async (a, body, msg) => { await api.put(`/gallery/admin/albums/${a.id}`, body); toast.success(msg); load(); };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}" and ALL its photos & comments? This cannot be undone.`)) return;
    await api.delete(`/gallery/admin/albums/${a.id}`); toast.success("Album deleted"); load();
  };

  return (
    <div data-testid="gallery-control">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="text-[11px] text-zinc-500 max-w-xl">
          Each album is a tournament's public gallery at <span className="text-pb-cyan">/gallery</span> — visible to anyone, no login. Upload shots, set a cover, then hit Publish. Feature the best albums so they show as quick links on the gallery landing page.
        </p>
        <button data-testid="gal-new-album-btn" onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-1.5 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-[#BEF264] transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Album
        </button>
      </div>

      {creating && (
        <div className="border border-pb-cyan/40 bg-pb-cyan/5 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="gal-create-form">
          <select data-testid="gal-create-tourney" value={nt.tournament_id} onChange={(e) => setNt((s) => ({ ...s, tournament_id: e.target.value }))}
            className="bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none max-w-[260px]">
            <option value="">Link a tournament (optional)…</option>
            {tourneys.map((t) => <option key={t.id} value={t.id}>{`${t.name} — ${t.date}`}</option>)}
          </select>
          <input data-testid="gal-create-title" value={nt.title} onChange={(e) => setNt((s) => ({ ...s, title: e.target.value }))}
            placeholder="Album title (defaults to tournament name)"
            className="flex-1 min-w-[200px] bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white focus:outline-none focus:border-pb-cyan" />
          <button data-testid="gal-create-save" onClick={create}
            className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:opacity-90">Create</button>
        </div>
      )}

      {albums.length === 0 ? (
        <div className="border border-dashed border-pb-border p-8 text-center text-zinc-600 text-sm" data-testid="gal-empty">No albums yet — create one for your next tournament.</div>
      ) : albums.map((a) => (
        <div key={a.id} className="border border-pb-border/70 mb-2" data-testid={`gal-album-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
            {a.cover_url ? <img src={a.cover_url} alt="" className="w-12 h-9 object-cover border border-pb-border" />
              : <span className="w-12 h-9 border border-pb-border flex items-center justify-center text-zinc-700"><ImageIcon className="w-4 h-4" /></span>}
            <button data-testid={`gal-open-${a.id}`} onClick={() => setOpenId(openId === a.id ? null : a.id)} className="flex-1 min-w-0 text-left">
              <span className="font-bold text-sm text-white">{a.title}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">{a.date} · {a.media_count} live{a.pending_count ? ` · ${a.pending_count} pending` : ""} · {a.comment_count} comments</span>
            </button>
            {a.pending_count > 0 && <span className="bg-amber-500 text-black text-[9px] font-black px-1.5 py-0.5" data-testid={`gal-pending-badge-${a.id}`}>{a.pending_count} to approve</span>}
            <button data-testid={`gal-feature-${a.id}`} title={a.featured ? "Unfeature" : "Feature on landing page"}
              onClick={() => upd(a, { featured: !a.featured }, a.featured ? "Removed from featured" : "Featured on the gallery landing page")}
              className={`w-8 h-8 border flex items-center justify-center transition-colors ${a.featured ? "border-yellow-500 text-yellow-400 bg-yellow-500/10" : "border-pb-border text-zinc-600 hover:border-yellow-500 hover:text-yellow-400"}`}>
              <Star className={`w-4 h-4 ${a.featured ? "fill-yellow-400" : ""}`} />
            </button>
            <button data-testid={`gal-publish-${a.id}`}
              onClick={() => upd(a, { published: !a.published }, a.published ? "Unpublished — hidden from the public" : "Published — live for the world 🌍")}
              className={`text-[9px] font-black uppercase tracking-widest px-3 py-2 border transition-colors ${a.published ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/10" : "border-pb-border text-zinc-500 hover:border-[#A3E635] hover:text-[#A3E635]"}`}>
              {a.published ? "Published" : "Draft — Publish"}
            </button>
            <button data-testid={`gal-delete-${a.id}`} onClick={() => del(a)} className="text-zinc-600 hover:text-pb-red transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
          {openId === a.id && <div className="px-3 pb-3"><AlbumManager album={a} onChanged={load} /></div>}
        </div>
      ))}
    </div>
  );
}
